import { useCallback, useEffect, useMemo, useState } from "react";
import type { AutocompleteOption } from "@/components/Autocomplete";
import { Autocomplete } from "@/components/Autocomplete";
import {
  SortableHeader,
  TableSortResetBar,
  sortRowsByKey,
  useTableSort,
} from "@/components/dataTable";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatDate } from "@/lib/format";
import { searchEmployees } from "@/lib/projectOverview";
import { normalizeEmployeeLogin } from "@/lib/auth";
import {
  assignWorkTimeModel,
  listWorkTimeAssignments,
  removeWorkTimeAssignment,
  type WorkTimeModel,
  type WorkTimeModelAssignment,
} from "@/lib/workTimeModels";

interface AssignmentsPanelProps {
  models: WorkTimeModel[];
  refreshKey: number;
  onChanged: () => void;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateDe(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

export function AssignmentsPanel({ models, refreshKey, onChanged }: AssignmentsPanelProps) {
  const [assignments, setAssignments] = useState<WorkTimeModelAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<{
    login: string;
    display: string;
  } | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<number | "">("");
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [assigning, setAssigning] = useState(false);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const { sort, toggle, reset } = useTableSort();
  const sortedAssignments = useMemo(
    () =>
      sortRowsByKey(assignments, sort, (row, key) => {
        switch (key) {
          case "employee":
            return row.user_display || row.user_login;
          case "model":
            return row.model_name;
          case "effective_from":
            return row.effective_from;
          case "assigned_at":
            return row.assigned_at;
          default:
            return null;
        }
      }),
    [assignments, sort],
  );

  const defaultModel = models.find((m) => m.is_default);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listWorkTimeAssignments();
      setAssignments(rows);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Zuweisungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (defaultModel && selectedModelId === "") {
      setSelectedModelId(defaultModel.id);
    }
  }, [defaultModel, selectedModelId]);

  const handleAssign = async () => {
    if (!selectedEmployee || !selectedModelId || !effectiveFrom) {
      setError("Mitarbeiter, Modell und Gültig-ab-Datum auswählen.");
      return;
    }
    setAssigning(true);
    setError(null);
    try {
      await assignWorkTimeModel(selectedEmployee.login, {
        model_id: Number(selectedModelId),
        user_display: selectedEmployee.display,
        effective_from: effectiveFrom,
      });
      setSelectedEmployee(null);
      onChanged();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Zuweisung fehlgeschlagen.");
    } finally {
      setAssigning(false);
    }
  };

  const handleRemove = async (login: string, from: string) => {
    if (
      !window.confirm(
        `Zuweisung für ${login} ab ${formatDateDe(from)} entfernen?`,
      )
    ) {
      return;
    }
    const key = `${login}|${from}`;
    setRemovingKey(key);
    try {
      await removeWorkTimeAssignment(login, from.slice(0, 10));
      onChanged();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Entfernen fehlgeschlagen.");
    } finally {
      setRemovingKey(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
        <h3 className="text-sm font-semibold text-label">Mitarbeiter zuweisen</h3>
        <p className="mt-1 text-xs text-label-secondary">
          Ohne Zuweisung gilt automatisch das Standardmodell
          {defaultModel ? ` („${defaultModel.name}")` : ""}. Modellwechsel gelten ab dem
          gewählten Datum.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
          <Autocomplete<{ login: string; display: string }>
            label="Mitarbeiter"
            placeholder="Name suchen…"
            minChars={0}
            loadOptions={async (q) => {
              const rows = await searchEmployees(q, 20);
              return rows.map(
                (row): AutocompleteOption<{ login: string; display: string }> => ({
                  key: row.id,
                  label: row.label || row.name || row.id,
                  description: row.subtitle,
                  value: {
                    login: normalizeEmployeeLogin(row.id),
                    display: row.label || row.name || row.id,
                  },
                }),
              );
            }}
            onSelect={(opt) => setSelectedEmployee(opt ? opt.value : null)}
          />
          <label className="space-y-1">
            <span className="eh-label">Arbeitszeitmodell</span>
            <select
              className="eh-input w-full"
              value={selectedModelId}
              onChange={(e) =>
                setSelectedModelId(e.target.value ? Number(e.target.value) : "")
              }
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.is_default ? " (Standard)" : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1">
            <span className="eh-label">Gültig ab</span>
            <input
              type="date"
              className="eh-input w-full"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </label>
          <div className="flex items-end">
            <Button
              variant="primary"
              className="w-full"
              disabled={assigning || !selectedEmployee || !effectiveFrom}
              onClick={() => void handleAssign()}
            >
              Zuweisen
            </Button>
          </div>
        </div>
      </div>

      {error && <Alert variant="error" title="Fehler">{error}</Alert>}

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : assignments.length === 0 ? (
        <div className="rounded-ios border border-dashed border-separator bg-card p-8 text-center text-sm text-label-secondary">
          Noch keine individuellen Zuweisungen — alle Mitarbeiter nutzen das Standardmodell.
        </div>
      ) : (
        <>
          <TableSortResetBar visible={sort != null} onReset={reset} />
          <div className="overflow-x-auto rounded-ios border border-separator bg-card shadow-ios">
          <table className="eh-data-table w-full min-w-[720px]">
            <thead>
              <tr>
                <th><SortableHeader label="Mitarbeiter" columnKey="employee" sort={sort} onSort={toggle} /></th>
                <th><SortableHeader label="Modell" columnKey="model" sort={sort} onSort={toggle} /></th>
                <th><SortableHeader label="Gültig ab" columnKey="effective_from" sort={sort} onSort={toggle} /></th>
                <th><SortableHeader label="Gesetzt am" columnKey="assigned_at" sort={sort} onSort={toggle} /></th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sortedAssignments.map((row) => {
                const key = `${row.user_login}|${row.effective_from}`;
                return (
                  <tr key={key}>
                    <td>
                      <div className="font-medium">{row.user_display || row.user_login}</div>
                      <div className="text-xs text-label-secondary">{row.user_login}</div>
                    </td>
                    <td>{row.model_name}</td>
                    <td>{formatDateDe(row.effective_from)}</td>
                    <td>{row.assigned_at ? formatDate(row.assigned_at) : "—"}</td>
                    <td className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={removingKey === key}
                        onClick={() => void handleRemove(row.user_login, row.effective_from)}
                      >
                        Entfernen
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
